import { Button } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import BasicTableTab from "features/commitments/tabs/BasicTableTab";
import { InternalAddCustomerModal } from "features/internal/provisioning/modals/InternalAddCustomerModal";
import {
  getInternalProvisioningCustomersTableDataRetrieveQueryKey,
  useInternalProvisioningCustomersTableDataRetrieveSuspense,
} from "gen/endpoints/internal/internal";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { rootRoute } from "shared/navigation/Routes";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

export const InternalProvisioningCustomersTab: React.FC = () => {
  const { t } = useTranslation(["internal"]);
  const queryClient = useQueryClient();
  const { data: tableData } =
    useInternalProvisioningCustomersTableDataRetrieveSuspense();

  const {
    value: isCustomerModalOpen,
    setTrue: openCustomerModal,
    setFalse: closeCustomerModal,
  } = useBoolean();

  const onSuccessComplete = () =>
    invalidateQueryKeys(
      queryClient,
      getInternalProvisioningCustomersTableDataRetrieveQueryKey(),
    );

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) =>
      row.data?.metadata?.type === "id_row"
        ? {
            type: "generic",
            to: rootRoute({})
              .internal({})
              .customer({ customerId: row.data.metadata.id }).$,
          }
        : undefined,
    [],
  );

  return (
    <>
      <BasicTableTab
        tableTitle={t("internal:customers")}
        data={tableData}
        getLinkInfoForRow={getLinkInfoForRow}
        dataTableBoxActions={
          <DataTableBoxActions>
            <Button size="xs" onClick={openCustomerModal}>
              {t("internal:modal.createCustomer.title")}
            </Button>
          </DataTableBoxActions>
        }
      />
      {isCustomerModalOpen && (
        <InternalAddCustomerModal
          onSuccessComplete={onSuccessComplete}
          onCloseComplete={closeCustomerModal}
        />
      )}
    </>
  );
};
